import { Dispatch, FormEvent, SetStateAction, useEffect, useState } from 'react';
import { PersonalInformation } from '../types/PersonalInformation';
import styles from './personalInformationForm.module.scss';
import FormInput from '../FormInput/FormInput';
import globalStyles from '../App.module.scss';
import {
    isValid,
    isValidHusstand,
    validateAddress,
    validateDateOfBirth,
    validateEmail,
    validateFirstname,
    validateFreetext,
    validateLastname,
    validatePhoneNumber,
    validatePostalcode,
} from './validation';
import { Medlemskapstype } from '../api/medlemskapAPI';
import { PersonalInformationFieldName } from './PersonalInformationFieldName';
import TermsLink from './TermsLink';
import { MedlemskapTypeId } from '../MedlemskapType/MedlemskapTypeId';
import { formatPhoneNumber } from './Helpers';
import PaymentSummaryWithDiscount from '../PaymentSummary/PaymentSummaryWithDiscount';
import PrivacyLink from '../PrivacyLink';
import { ApiResource } from '../api/ApiResource';
import { PostalCodeResponse } from '../api/postalCodeAPI';
import { useErrorPageRedirectIfFailed } from '../hooks';

interface PersonalInformationFormProps {
    postalCodesListResource: ApiResource<PostalCodeResponse>;
    formInput: PersonalInformation;
    setFormInput: Dispatch<SetStateAction<PersonalInformation>>;
    onSubmit: () => void;
    activeMedlemskap?: Medlemskapstype;
    discountCode: string;
    setDiscountCode: Dispatch<SetStateAction<string>>;
    discount: number;
    setDiscount: Dispatch<SetStateAction<number>>;
}

function PersonalInformationForm({
    postalCodesListResource,
    formInput,
    setFormInput,
    onSubmit,
    activeMedlemskap,
    discountCode,
    setDiscountCode,
    discount,
    setDiscount,
}: PersonalInformationFormProps) {
    const [formErrors, setFormErrors] = useState<PersonalInformation>({
        phoneNumber: '',
        firstname: '',
        lastname: '',
        email: '',
        address: '',
        postalcode: '',
        dateOfBirth: '',
        freetext: '',
    });
    const [postalCodes, setPostalCodes] = useState<string[]>([]);
    const [postalCodeValid, setPostalCodeValid] = useState<boolean>(false);

    useErrorPageRedirectIfFailed(postalCodesListResource);

    useEffect(() => {
        if (postalCodesListResource.status === 'ok') {
            setPostalCodes(postalCodesListResource.data.postalCodes);
        }
    }, [postalCodesListResource, setPostalCodes]);

    useEffect(() => {
        setDiscountCode('');
        setDiscount(0);
    }, [setDiscount, setDiscountCode]);

    const onChange = (event: FormEvent<HTMLInputElement>) => {
        setFormErrors({ ...formErrors, [event.currentTarget.name]: '' });
        setFormInput({
            ...formInput,
            [event.currentTarget.name]: event.currentTarget.value,
        });
    };

    const isHusstand = activeMedlemskap?.id === (MedlemskapTypeId.Husstand || MedlemskapTypeId.HusstandBarn);
    const onChangePhoneNumber = (event: FormEvent<HTMLInputElement>) => {
        setFormErrors({ ...formErrors, phoneNumber: '' });
        const formattedPhoneNumber = formatPhoneNumber(event.currentTarget.value);
        if (formattedPhoneNumber === '') return;
        setFormInput({
            ...formInput,
            phoneNumber: formattedPhoneNumber,
        });
    };

    const onChangeNewsletter = () => {
        setFormInput({
            ...formInput,
            newsletter: !formInput.newsletter,
        });
    };

    const onChangeDateOfBirth = (event: FormEvent<HTMLInputElement>) => {
        setFormErrors({ ...formErrors, [event.currentTarget.name]: '' });
        let newDate = event.currentTarget.value;
        const currentDate = formInput.dateOfBirth;
        if (
            newDate.length > 10 ||
            !newDate.match(/^[\d|.]*$/) ||
            newDate.match(/^\.|^\d\.|\.\d\.|\.\.|(\.(.*)){3,}/) ||
            !(
                newDate === currentDate.substring(0, newDate.length) ||
                currentDate === newDate.substring(0, currentDate.length)
            )
        ) {
            return;
        }
        if (newDate.match(/^\d\d$|^\d\d\.\d\d$/)) {
            if (currentDate.match(/^\d\d\.$|^\d\d\.\d\d\.$/)) {
                newDate = newDate.slice(0, -1);
            } else {
                newDate = newDate.concat('.');
            }
        }
        setFormInput({
            ...formInput,
            [event.currentTarget.name]: newDate,
        });
    };

    const onChangePostalCode = (event: FormEvent<HTMLInputElement>) => {
        setFormErrors({ ...formErrors, [event.currentTarget.name]: '' });
        if (postalCodes.length > 0) {
            setPostalCodeValid(postalCodes.includes(event.currentTarget.value));
        }
        setFormInput({
            ...formInput,
            [event.currentTarget.name]: event.currentTarget.value,
        });
    };

    const setValidationError = (
        fieldName: PersonalInformationFieldName,
        value: string,
        validator: (value: string) => string
    ) => {
        setFormErrors({
            ...formErrors,
            [fieldName]: validator(value),
        });
    };

    const validatePostalCodeWithListCheck = (value: string) => {
        const simpleValidation = validatePostalcode(value);
        if (simpleValidation !== '') {
            return simpleValidation;
        }
        if (!postalCodeValid) {
            return 'Ikke gyldig postnummer';
        }
        return '';
    };

    const setAllValidationErrors = () => {
        if (isHusstand) {
            setFormErrors({
                firstname: validateFirstname(formInput.firstname),
                lastname: validateLastname(formInput.lastname),
                phoneNumber: validatePhoneNumber(formInput.phoneNumber),
                email: validateEmail(formInput.email),
                address: validateAddress(formInput.address),
                postalcode: validatePostalCodeWithListCheck(formInput.postalcode),
                dateOfBirth: validateDateOfBirth(formInput.dateOfBirth),
                freetext: validateFreetext(formInput.freetext),
            });
        } else {
            setFormErrors({
                firstname: validateFirstname(formInput.firstname),
                lastname: validateLastname(formInput.lastname),
                phoneNumber: validatePhoneNumber(formInput.phoneNumber),
                email: validateEmail(formInput.email),
                address: validateAddress(formInput.address),
                postalcode: validatePostalCodeWithListCheck(formInput.postalcode),
                dateOfBirth: validateDateOfBirth(formInput.dateOfBirth),
            });
        }
    };
    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (isHusstand && isValidHusstand(formInput) && postalCodeValid) {
            onSubmit();
        } else if (!isHusstand && isValid(formInput) && postalCodeValid) {
            onSubmit();
        } else {
            setAllValidationErrors();
        }
    };

    if (postalCodesListResource.status !== 'ok') {
        return null;
    }

    return (
        <div>
            <form onSubmit={handleSubmit} noValidate={true} className={styles.form}>
                <FormInput
                    label={'Fødselsdato (dd.mm.åååå)'}
                    fieldName={'dateOfBirth'}
                    validateField={(fieldName, value) => setValidationError(fieldName, value, validateDateOfBirth)}
                    onChange={onChangeDateOfBirth}
                    inputValue={formInput.dateOfBirth}
                    errorMessage={formErrors.dateOfBirth}
                    inputType="text"
                />
                <FormInput
                    label={'Fornavn'}
                    fieldName={'firstname'}
                    validateField={(fieldName, value) => setValidationError(fieldName, value, validateFirstname)}
                    onChange={onChange}
                    inputValue={formInput.firstname}
                    errorMessage={formErrors.firstname}
                    inputType="text"
                />
                <FormInput
                    label={'Etternavn'}
                    fieldName={'lastname'}
                    validateField={(fieldName, value) => setValidationError(fieldName, value, validateLastname)}
                    onChange={onChange}
                    inputValue={formInput.lastname}
                    errorMessage={formErrors.lastname}
                    inputType="text"
                />
                <FormInput
                    label={'Mobilnummer'}
                    fieldName={'phoneNumber'}
                    validateField={(fieldName, value) => setValidationError(fieldName, value, validatePhoneNumber)}
                    onChange={onChangePhoneNumber}
                    inputValue={formInput.phoneNumber}
                    errorMessage={formErrors.phoneNumber}
                    inputType="tel"
                />
                <FormInput
                    label={'E-postadresse'}
                    fieldName={'email'}
                    validateField={(fieldName, value) => setValidationError(fieldName, value, validateEmail)}
                    onChange={onChange}
                    inputValue={formInput.email}
                    errorMessage={formErrors.email}
                    inputType="email"
                />
                <FormInput
                    label={'Adresse'}
                    fieldName={'address'}
                    validateField={(fieldName, value) => setValidationError(fieldName, value, validateAddress)}
                    onChange={onChange}
                    inputValue={formInput.address}
                    errorMessage={formErrors.address}
                    inputType="text"
                />
                <FormInput
                    label={'Postnummer'}
                    fieldName={'postalcode'}
                    validateField={(fieldName, value) => setValidationError(fieldName, value, validatePostalcode)}
                    onChange={onChangePostalCode}
                    inputValue={formInput.postalcode}
                    errorMessage={formErrors.postalcode}
                    inputType="number"
                />
                <div className={styles.checkboxContainer}>
                    <input
                        type="checkbox"
                        className={styles.checkbox}
                        id={'newsletter'}
                        onChange={onChangeNewsletter}
                    />
                    <label htmlFor={'newsletter'} className={styles.newsletterText}>
                        Jeg vil gjerne motta nyhetsbrev fra Skiforeningen.
                    </label>
                </div>
                {activeMedlemskap?.id === (MedlemskapTypeId.Husstand || MedlemskapTypeId.HusstandBarn) && (
                    <div className={styles.freetextBox}>
                        <FormInput
                            label={'Hovedmedlem'}
                            fieldName={'freetext'}
                            validateField={(fieldName, value) => setValidationError(fieldName, value, validateFreetext)}
                            onChange={onChange}
                            inputValue={formInput.freetext}
                            errorMessage={formErrors.freetext ?? ''}
                            inputType="text"
                        />
                    </div>
                )}

                <div className={globalStyles.summary}>
                    {activeMedlemskap && (
                        <PaymentSummaryWithDiscount
                            medlemskapstype={activeMedlemskap}
                            discountCode={discountCode}
                            discount={discount}
                            setDiscountCode={setDiscountCode}
                            setDiscount={setDiscount}
                        />
                    )}
                    <TermsLink />
                    <button type={'submit'} className={globalStyles.standardButton}>
                        Til betaling
                    </button>
                </div>
                <PrivacyLink />
            </form>
        </div>
    );
}

export default PersonalInformationForm;
